import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import PublicLayout from '@/components/layouts/PublicLayout';
import AppLayout from '@/components/layouts/AppLayout';
import HostFullscreenLayout from '@/components/layouts/HostFullscreenLayout';
import AdminLayout from '@/components/layouts/AdminLayout';
import RequireAuth from './guards/RequireAuth';
import RequireSubscription from './guards/RequireSubscription';
import RequireAdmin from './guards/RequireAdmin';
import RequireEmailVerified from './guards/RequireEmailVerified';
import ScrollToTop from './ScrollToTop';
import RouteErrorBoundary from './RouteErrorBoundary';
import CookieConsentBanner from '@/components/CookieConsentBanner';
import ToastContainer from '@/components/ui/Toast/ToastContainer';
import PacksPurchase from '@/screens/Ateliers/PacksPurchase';
import PaymentSuccess from '@/screens/Ateliers/PaymentSuccess';
import ExportWeekPDF from '../screens/AnimationsPlanning/ExportWeek';
import ExportMonthPDF from '../screens/AnimationsPlanning/ExportMonth';

// Lazy-loaded screens
const HomeScreen = lazy(() => import('@/screens/Home'));
const LoginScreen = lazy(() => import('@/screens/auth/Login'));
const RegisterScreen = lazy(() => import('@/screens/auth/Register'));
const ForgotPasswordScreen = lazy(() => import('@/screens/auth/ForgotPassword'));
const VerifyEmailScreen = lazy(() => import('@/screens/auth/VerifyEmail'));
const VerifyEmailHandler = lazy(() => import('@/screens/auth/VerifyEmailHandler'));
const ResetPasswordHandler = lazy(() => import('@/screens/auth/ResetPasswordHandler'));

const DashboardScreen = lazy(() => import('@/screens/Dashboard'));
const AteliersScreen = lazy(() => import('@/screens/Ateliers'));
const AnimationsPlanningScreen = lazy(() => import('@/screens/AnimationsPlanning'));
const ManualBuilderScreen = lazy(() => import('@/screens/builder/Manual'));
const AIGeneratorScreen = lazy(() => import('@/screens/CreateWithAI'));
const CommunityScreen = lazy(() => import('@/screens/Community'));
const QuizPublicDetailScreen = lazy(() => import('@/screens/Community/QuizDetail'));

const HostLobbyScreen = lazy(() => import('@/screens/host/Lobby'));
const HostInteractiveScreen = lazy(() => import('@/screens/host/Interactive'));
const HostSlideshowScreen = lazy(() => import('@/screens/host/Slideshow'));

const PlayerEntryScreen = lazy(() => import('@/screens/player/Entry'));
const PlayerGameScreen = lazy(() => import('@/screens/player/Game'));

const AccountScreen = lazy(() => import('@/screens/Account'));
const AccountSubscriptionScreen = lazy(() => import('@/screens/AccountSubscription'));

const AdminDashboard = lazy(() => import('@/screens/admin/Dashboard'));
const AdminQuizzes = lazy(() => import('@/screens/admin/Quizzes'));
const AdminUsers = lazy(() => import('@/screens/admin/Users'));
const AdminThemes = lazy(() => import('@/screens/admin/Themes'));
const AdminReports = lazy(() => import('@/screens/admin/Reports'));

const LegalPage = lazy(() => import('@/screens/LegalPage'));
const PDFViewerScreen = lazy(() => import('@/screens/PDFViewer'));

const AccessDeniedScreen = lazy(() => import('@/screens/system/AccessDeniedScreen'));
const NotFoundScreen = lazy(() => import('@/screens/system/NotFoundScreen'));

/**
 * TitleSetter Component
 * Sets document.title on route changes
 */
function TitleSetter({ title }) {
  const location = useLocation();
  useEffect(() => {
    if (title) document.title = title;
  }, [title, location.pathname]);
  return null;
}

/**
 * LoadingFallback Component
 * Shown while lazy-loaded screens are loading
 */
function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-2">
        <div className="inline-block animate-spin text-4xl" aria-hidden="true">
          ⟳
        </div>
        <p className="text-neutral-600">Chargement…</p>
      </div>
    </div>
  );
}

/**
 * AppRouter
 * Main router with nested layouts and route guards
 */
export default function AppRouter() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public layout */}
          <Route element={<PublicLayout />} errorElement={<RouteErrorBoundary />}>
            <Route
              path="/"
              element={
                <>
                  <TitleSetter title="AnimFacile — Quiz pour résidences seniors" />
                  <HomeScreen />
                </>
              }
            />
            <Route path="/auth">
              <Route
                path="login"
                element={
                  <>
                    <TitleSetter title="Connexion — AnimFacile" />
                    <LoginScreen />
                  </>
                }
              />
              <Route
                path="register"
                element={
                  <>
                    <TitleSetter title="Inscription — AnimFacile" />
                    <RegisterScreen />
                  </>
                }
              />
              <Route
                path="forgot"
                element={
                  <>
                    <TitleSetter title="Mot de passe oublié — AnimFacile" />
                    <ForgotPasswordScreen />
                  </>
                }
              />
              <Route
                path="verify"
                element={
                  <>
                    <TitleSetter title="Verification email — AnimFacile" />
                    <VerifyEmailScreen />
                  </>
                }
              />
            </Route>

            {/* Email verification from Brevo */}
            <Route
              path="/verify-email"
              element={
                <>
                  <TitleSetter title="Verification email — AnimFacile" />
                  <VerifyEmailHandler />
                </>
              }
            />

            {/* Password reset from Brevo */}
            <Route
              path="/reset-password"
              element={
                <>
                  <TitleSetter title="Reinitialisation mot de passe — AnimFacile" />
                  <ResetPasswordHandler />
                </>
              }
            />

            <Route
              path="/quizzes/:id"
              element={
                <>
                  <TitleSetter title="Quiz — AnimFacile" />
                  <QuizPublicDetailScreen />
                </>
              }
            />

            {/* Legal pages */}
            <Route
              path="/legal/:page"
              element={
                <>
                  <TitleSetter title="Informations légales — AnimFacile" />
                  <LegalPage />
                </>
              }
            />
          </Route>

          {/* Player public routes (no layout wrapper) */}
          <Route
            path="/play"
            element={
              <>
                <TitleSetter title="Rejoindre — AnimFacile" />
                <PlayerEntryScreen />
              </>
            }
          />
          <Route
            path="/c/:code"
            element={
              <>
                <TitleSetter title="Rejoindre — AnimFacile" />
                <PlayerEntryScreen />
              </>
            }
          />

          {/* Host authenticated layout */}
          <Route
            element={
              <RequireAuth>
                <RequireEmailVerified>
                  <AppLayout />
                </RequireEmailVerified>
              </RequireAuth>
            }
          >
            <Route
              path="/dashboard"
              element={
                <>
                  <TitleSetter title="Mes Quiz — AnimFacile" />
                  <DashboardScreen />
                </>
              }
            />
            <Route
              path="/ateliers"
              element={
                <>
                  <TitleSetter title="Mes Ateliers — AnimFacile" />
                  <AteliersScreen />
                </>
              }
            />
            <Route
              path="/ateliers/packs-purchase"
              element={
                <>
                  <TitleSetter title="Packs d'Ateliers — AnimFacile" />
                  <PacksPurchase />
                </>
              }
            />
            <Route
              path="/ateliers/payment-success"
              element={
                <>
                  <TitleSetter title="Achat Réussi — AnimFacile" />
                  <PaymentSuccess />
                </>
              }
            />
            <Route
              path="/animations"
              element={
                <>
                  <TitleSetter title="Planning d'Animations — AnimFacile" />
                  <AnimationsPlanningScreen />
                </>
              }
            />
            <Route
              path="/animations/week-export"
              element={
                <>
                  <TitleSetter title="Planning d'Animations — Export Hebdomadaire" />
                  <ExportWeekPDF />
                </>
              }
            />
            <Route
              path="/animations/month-export"
              element={
                <>
                  <TitleSetter title="Planning d'Animations — Export Mensuel" />
                  <ExportMonthPDF />
                </>
              }
            />
            <Route
              path="/quizzes/new"
              element={
                <RequireSubscription>
                  <TitleSetter title="Créer un quiz — AnimFacile" />
                  <ManualBuilderScreen />
                </RequireSubscription>
              }
            />
            <Route
              path="/quizzes/:id/edit"
              element={
                <RequireSubscription>
                  <TitleSetter title="Modifier le quiz — AnimFacile" />
                  <ManualBuilderScreen />
                </RequireSubscription>
              }
            />
            <Route
              path="/quizzes/ai/new"
              element={
                <RequireSubscription>
                  <TitleSetter title="Créer avec IA — AnimFacile" />
                  <AIGeneratorScreen />
                </RequireSubscription>
              }
            />
            <Route
              path="/account"
              element={
                <>
                  <TitleSetter title="Mon compte — AnimFacile" />
                  <AccountScreen />
                </>
              }
            />
            <Route
              path="/account/subscription"
              element={
                <>
                  <TitleSetter title="Compte & Abonnement — AnimFacile" />
                  <AccountSubscriptionScreen />
                </>
              }
            />
            <Route
              path="/community"
              element={
                <>
                  <TitleSetter title="Communauté — AnimFacile" />
                  <CommunityScreen />
                </>
              }
            />
          </Route>

          {/* Host fullscreen projector routes */}
          <Route
            element={
              <RequireAuth>
                <RequireSubscription>
                  <HostFullscreenLayout />
                </RequireSubscription>
              </RequireAuth>
            }
          >
            <Route
              path="/host/:sessionId"
              element={
                <>
                  <TitleSetter title="Lobby — AnimFacile" />
                  <HostLobbyScreen />
                </>
              }
            />
            <Route
              path="/host/:sessionId/interactive"
              element={
                <>
                  <TitleSetter title="Mode Interactif — AnimFacile" />
                  <HostInteractiveScreen />
                </>
              }
            />
            <Route
              path="/host/:sessionId/slideshow"
              element={
                <>
                  <TitleSetter title="Mode Diaporama — AnimFacile" />
                  <HostSlideshowScreen />
                </>
              }
            />
          </Route>

          {/* Player game (public, no layout) */}
          <Route
            path="/play/:sessionId"
            element={
              <>
                <TitleSetter title="Jeu — AnimFacile" />
                <PlayerGameScreen />
              </>
            }
          />

          {/* PDF Viewer (authenticated, fullscreen, no layout) */}
          <Route
            path="/ateliers/pdf-viewer"
            element={
              <RequireAuth>
                <RequireEmailVerified>
                  <TitleSetter title="Atelier PDF — AnimFacile" />
                  <PDFViewerScreen />
                </RequireEmailVerified>
              </RequireAuth>
            }
          />

          {/* Admin */}
          <Route
            element={
              <RequireAuth>
                <RequireAdmin>
                  <AdminLayout />
                </RequireAdmin>
              </RequireAuth>
            }
          >
            <Route
              path="/admin"
              element={
                <>
                  <TitleSetter title="Admin Dashboard — AnimFacile" />
                  <AdminDashboard />
                </>
              }
            />
            <Route
              path="/admin/quizzes"
              element={
                <>
                  <TitleSetter title="Admin Quiz — AnimFacile" />
                  <AdminQuizzes />
                </>
              }
            />
            <Route
              path="/admin/users"
              element={
                <>
                  <TitleSetter title="Admin Utilisateurs — AnimFacile" />
                  <AdminUsers />
                </>
              }
            />
            <Route
              path="/admin/themes"
              element={
                <>
                  <TitleSetter title="Admin Thèmes — AnimFacile" />
                  <AdminThemes />
                </>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <>
                  <TitleSetter title="Admin Signalements — AnimFacile" />
                  <AdminReports />
                </>
              }
            />
          </Route>

          {/* System */}
          <Route
            path="/403"
            element={
              <>
                <TitleSetter title="Accès refusé — AnimFacile" />
                <AccessDeniedScreen />
              </>
            }
          />
          <Route
            path="*"
            element={
              <>
                <TitleSetter title="Page introuvable — AnimFacile" />
                <NotFoundScreen />
              </>
            }
          />
        </Routes>
      </Suspense>
      <ToastContainer />
      <CookieConsentBanner />
    </BrowserRouter>
  );
}
